// libraries
import * as React from 'react'
import { Col, Row, Button } from 'react-bootstrap'
import { DateTime } from 'luxon'
import { Auth } from 'aws-amplify'

// definitions
import * as AppDefinitions from '../../App.d'
import { TermsVersion } from '../../../../back-end/common/termsVersion'
import { TermsAcceptance } from '../../../../back-end/common/termsAcceptance'
import { User } from '../../../../back-end/common/users'

// components
import { Dialog } from '../../components/ui/dialog/dialog'
import * as Messages from '../../components/ui/messages/messages'
import { ModalListChildProps } from '../ui/modalList/modalList'

// utilities
import * as Request from '../../utilities/request'

interface TermsAndConditionsModalProps extends ModalListChildProps {
	updateShownModal: React.Dispatch<React.SetStateAction<boolean>>
	appState: AppDefinitions.AppState
	product: string
	latestTermsVersion: TermsVersion
	denialRecord?: TermsAcceptance
	denialUser?: User
	type: 'ChooseOption' | 'DeniedAlert'
}

const getTermsLinkByProduct = (product: string) => {
	switch (product) {
		case 'LeaveCal':
			return 'https://help.finlert.com/leavecal/terms-conditions'
		case 'SubSync':
			return 'https://help.finlert.com/subsync/terms-conditions-for-subsync'
		default:
			return 'https://help.finlert.com/'
	}
}

const TermsAndConditionsModal = (props: TermsAndConditionsModalProps) => {
	const [showDialog, updateShowDialog] = React.useState<boolean>(false)
	const [messages, updateMessages] = Messages.useMessageReducer([])

	const handleDenyTerms = async () => {
		const denial = {
			userID: props.appState!.authState!.attributes!['custom:userId'],
			accountID: props.appState!.authState!.attributes!['custom:currentAccountId'],
			termsVersionID: props.latestTermsVersion.termsVersionID,
			termsAcceptanceDate: DateTime.utc(),
			termsAcceptanceStatus: 'denied',
		}

		try {
			const result = await Request.post(`termsacceptance/${props.product.toLocaleLowerCase()}`, denial, props.appState.authState)
			if (result.data.success) {
				updateMessages(Messages.addMessage('success', 'Successfully denied terms and conditions'))
				props.updateShownModal(false)
				window.location.reload() // need to refresh
			} else {
				updateMessages(Messages.addMessage('danger', 'Unable to deny terms and conditions'))
			}
		} catch (e) {
			updateMessages(Messages.addMessage('danger', 'Unable to deny terms and conditions'))
		}
	}

	const handleAcceptTerms = async () => {
		const acceptance = {
			userID: props.appState!.authState!.attributes!['custom:userId'],
			accountID: props.appState!.authState!.attributes!['custom:currentAccountId'],
			termsVersionID: props.latestTermsVersion.termsVersionID,
			termsAcceptanceDate: DateTime.now(),
			termsAcceptanceStatus: 'accepted',
		}

		try {
			const result = await Request.post(`termsacceptance/${props.product.toLocaleLowerCase()}`, acceptance, props.appState.authState)
			if (result.data.success) {
				updateMessages(Messages.addMessage('success', 'Successfully accepted terms and conditions'))
				props.updateShownModal(false)
			} else {
				updateMessages(Messages.addMessage('danger', 'Unable to accept terms and conditions'))
			}
		} catch (e) {
			updateMessages(Messages.addMessage('danger', 'Unable to accept terms and conditions'))
		}
	}

	if (props.type === 'ChooseOption') {
		return (
			<div>
				<Row>
					<Col>
						<h3>Terms and Conditions</h3>
					</Col>
				</Row>
				<Row className="py-2">
					<Col>
						Please review our latest Terms and Conditions before continuing to use our services. By accepting, you agree to comply with our updated
						terms, which cover how we handle your data, service usage, and other important information.
					</Col>
				</Row>
				<Row className="py-2">
					<Col>
						<a target="_blank" href={getTermsLinkByProduct(props.product)} rel="noreferrer">
							View Full Terms and Conditions
						</a>
					</Col>
				</Row>
				<Row>
					<Col>Do you accept the Terms and Conditions?</Col>
				</Row>
				<Row className="py-2 px-3">
					<Button
						variant="success"
						className="px-3 py-2"
						onClick={() => {
							handleAcceptTerms()
						}}
					>
						Accept
					</Button>

					<Col xs="auto">
						<Button
							variant="secondary"
							className="px-3 py-2"
							onClick={() => {
								updateShowDialog(true)
							}}
						>
							Deny
						</Button>
					</Col>
				</Row>
				<Messages.Messages messages={messages} updateMessage={updateMessages} />
				<Dialog
					centered={true}
					show={showDialog}
					handleClose={() => updateShowDialog(false)}
					handleConfirm={handleDenyTerms}
					titleText={'Service Notice'}
					bodyText={
						<>
							You have chosen to decline the Terms and Conditions. As a result, we are unable to continue providing our services to you. If you
							change your mind, you may contact our support team in the future at <a href="mailto:support@finlert.com">support@finlert.com</a>.
							Are you sure you want to deny?
						</>
					}
				/>
			</div>
		)
	} else if (props.type === 'DeniedAlert' && props.denialRecord) {
		return (
			<div>
				<Row>
					<Col>
						<h5>Service Revoked: Terms and Conditions not Accepted</h5>
					</Col>
				</Row>
				<Row>
					<Col
						style={{ whiteSpace: 'pre-wrap' }}
						dangerouslySetInnerHTML={{
							__html: `${
								props.denialUser ? `${props.denialUser.firstName} ${props.denialUser.lastName}` : `User with ID: ${props.denialRecord.userID}`
							} declined the latest Terms and Conditions on ${DateTime.fromISO(props.denialRecord.termsAcceptanceDate).toFormat(
								'dd/MM/yyyy'
							)}. As a result, this account can no longer access our services. If this was done in error, or if you wish to regain access, please contact our support team at <a href="mailto:support@finlert.com">support@finlert.com</a>.`,
						}}
					/>
				</Row>
				<Row className="mt-3">
					<Col className="d-flex justify-content-end">
						<Button
							onClick={() => {
								Auth.signOut()
									.then(() => {
										window.location.href = '/'
									})
									.catch((err: Error) => console.log(err))
							}}
						>
							Log out
						</Button>
					</Col>
				</Row>
			</div>
		)
	}
	return <></>
}

export default TermsAndConditionsModal
